<template>
  <div data-testid="staff-transactions-table">
    <data-table
      ref="datatableRef"
      test-id-prefix="staff-transactions"
      :config="tableConfig"
      :data="rows"
      display-mode="table"
      :refreshButton="{ show: true }"
      @refresh="emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'

const { t } = useI18n()

export interface AttemptDisplayRow {
  // tx identity
  id?: string | number
  franquia_id: number
  franchise_name?: string
  kind?: string
  /** Row currency (from contract, defaults to 'BRL'). */
  currency?: string
  /** amount_cents converted to major units via currencyFactor for that currency. */
  amount_brl?: number
  /** Pre-formatted amount string via n() with row currency — use for display. */
  amount_display?: string
  provider?: string
  provider_charge_id?: string
  // attempt fields
  status?: string
  created_at_display?: string
  paid_at_display?: string
  // stable row key (tx.id + attempt index)
  _row_key: string
}

interface Props {
  rows: AttemptDisplayRow[]
}

defineProps<Props>()

const emit = defineEmits<{
  refresh: []
}>()

interface DataTableExposed {
  loadData: () => void
}

const datatableRef = ref<DataTableExposed | null>(null)

const kindBadgeConfig = (row: Record<string, unknown>) => {
  const kind = row.kind as string | undefined
  const map: Record<string, { label: string; severity: string }> = {
    cycle:           { label: t('Mensalidade'),    severity: 'info' },
    setup_fee:       { label: t('Taxa de implantação'), severity: 'secondary' },
    credit_purchase: { label: t('Créditos IA'),    severity: 'success' },
  }
  const cfg = kind ? (map[kind] ?? { label: kind, severity: 'secondary' }) : { label: '—', severity: 'secondary' }
  return { label: cfg.label, severity: cfg.severity, tooltip: cfg.label }
}

// Shared by the badge renderer AND the filter dropdown (S9-05) — one source
// of truth for which raw status values exist and what staff sees for them.
const statusBadgeMap = computed<Record<string, { label: string; severity: string }>>(() => ({
  pending:    { label: t('Pendente'),   severity: 'warn' },
  confirmed:  { label: t('Confirmada'), severity: 'success' },
  paid:       { label: t('Paga'),       severity: 'success' },
  failed:     { label: t('Falhou'),     severity: 'danger' },
  overdue:    { label: t('Vencida'),    severity: 'danger' },

  // M4: an abandoned credit-purchase checkout (ai_credit_purchases.status='expired')
  // — distinct from 'failed' (attempted and refused) and from 'pending' (still open).
  expired:    { label: t('Abandonada'), severity: 'secondary' },
  // M1: a pending cycle line closed as 'superseded' by a newer transaction
  superseded: { label: t('Substituída'), severity: 'secondary' },
  // M3c (D5): money that landed AFTER the subscription was cancelled. The
  // platform refused to apply it and delivered nothing, so it is owed back —
  // 'danger' because it is an open liability a human has to act on, not a
  // settled outcome. Distinct from 'failed' on purpose: the money ARRIVED.
  refund_pending: { label: t('Estorno pendente'), severity: 'danger' },
  // ...and its only exit: a Full user acknowledged the refund they executed
  // in the gateway's own panel. Terminal, nothing left to do.
  refunded:       { label: t('Estornada'),        severity: 'secondary' },
}))

const statusBadgeConfig = (row: Record<string, unknown>) => {
  const status = row.status as string | undefined
  const cfg = status ? (statusBadgeMap.value[status] ?? { label: status, severity: 'secondary' }) : { label: '—', severity: 'secondary' }
  return { label: cfg.label, severity: cfg.severity, tooltip: cfg.label }
}

// S9-05: DataTable's select-type filter compares FilterMatchMode.EQUALS
// against the RAW row value, never the rendered badge label — a plain
// filterType:'text' input let staff type what they SEE ("Confirmada") and
// get zero rows back, because the row actually carries the machine code
// ("confirmed"). filterOptions pairs each raw value with its translated
// label so the dropdown shows what staff read on screen while still
// filtering on the value the row carries.
const statusFilterOptions = computed(() =>
  Object.entries(statusBadgeMap.value).map(([value, cfg]) => ({ label: cfg.label, value })),
)

function handleIdClick(_value: unknown, row: unknown): void {
  eventBus.emit('modal.staffTransaction.open', row)
}

const tableConfig = computed(() => ({
  tableId: 'tabelaStaffTransactions',
  title: t('Transações'),
  showFilters: true,
  filterDisplay: 'row',
  columns: [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: handleIdClick,
    },
    {
      data: 'franchise_name',
      title: t('Franquia'),
      sortable: true,
      filterType: 'text',
    },
    {
      data: 'kind',
      title: t('Tipo'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: kindBadgeConfig,
      filterType: 'text',
    },
    {
      data: 'currency',
      title: t('Moeda'),
      sortable: true,
      filterType: 'text',
    },
    {
      data: 'amount_display',
      title: t('Valor'),
      sortable: false,
    },
    {
      data: 'status',
      title: t('Situação'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: statusBadgeConfig,
      filterType: 'select',
      filterOptions: statusFilterOptions.value,
    },
    {
      data: 'provider',
      title: t('Provedor'),
      sortable: true,
      filterType: 'text',
    },
    {
      data: 'paid_at_display',
      title: t('Pago em'),
      sortable: false,
    },
    {
      data: 'created_at_display',
      title: t('Data da tentativa'),
      sortable: false,
    },
  ],
  footer: false,
  rowTestId: (row: Record<string, unknown>) => {
    const key = row?._row_key
    return key == null ? '' : `staff-transactions-row-${key as string}`
  },
}))
</script>
